import React, { useCallback, useState } from 'react';

import { colors } from '@/assets/styles/colors';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { CommentBox } from '@/components/ui/inputs';
import { Selector } from '@/components/ui/inputs/selector';
import { PermissionGuard, SidePanelFormLayout } from '@/components/layout';
import { Text } from '@/components/ui/text/Text';
import { useOvertimeJustificationTypes } from '@/hooks/overtime/useOvertimeJustificationTypes';
import { useUI } from '@/hooks/ui/useUI';
import { useTranslation } from '@/i18n';
import { normalizeNumericSelectValue } from '@/utils/formHelpers';
import { OVERTIME_JUSTIFICATION_FREE_COMMENT_MAX_LENGTH } from '@/api/domains/overtime/overtimeApproval.types';


export interface OvertimeJustifyPanelProps {
  /**
   * Colaboradores sobre los que se aplicará la justificación. Con uno solo se muestra su nombre
   * —el criterio de aceptación lo pide explícitamente—; con varios el conteo, porque la acción
   * admite selección múltiple y enumerar veinte nombres no aporta.
   */
  employeeNames: string[];
  /**
   * Ejecuta la justificación. Resuelve en `true` solo si el backend respondió correctamente; los
   * avisos de resultado los emite quien la provee (el hook de la pantalla), no este panel.
   */
  onConfirm: (justificationTypeId: number, freeComment: string | undefined) => Promise<boolean>;
}

/**
 * Panel lateral de justificación del revisor desde el listado general de Aprobación HHEE
 * (ASIS-5358). Reemplaza al modal que cumplía este rol: el catálogo de motivos y la acción viven
 * ahora en el panel derecho, como el resto de los formularios de la app (`AdjustMarkPanel`,
 * `BulkAdjustMarkPanel`).
 *
 * Carga el catálogo POR SÍ MISMO en vez de recibirlo por props. `openSidePanel` guarda los props en
 * el store al abrir, así que un catálogo que llega después —o un `isSubmitting` que cambia— no se
 * reflejaría: quedarían congelados en el valor que tenían al abrir. Por lo mismo el estado de envío
 * es local. Solo se monta en la vista del revisor, que es la única que ofrece la acción, así que el
 * `enabled` del catálogo es incondicional aquí (el endpoint responde 403 a otros perfiles).
 *
 * El comentario libre aparece solo cuando el tipo tiene código `OTRO`, y ahí es obligatorio: sin él
 * el backend rechaza la operación, así que ofrecer «OTRO» sin el campo dejaría esa opción
 * inutilizable. El cierre por X y por overlay lo maneja `SidePanelContainer`.
 */
export const OvertimeJustifyPanel: React.FC<OvertimeJustifyPanelProps> = ({
  employeeNames,
  onConfirm,
}) => {
  const { t: translate } = useTranslation();
  const { closeSidePanel } = useUI();

  const {
    options: typeOptions,
    isLoading: isLoadingTypes,
    isEmpty: hasNoTypes,
    requiresFreeComment,
  } = useOvertimeJustificationTypes(true);

  const [justificationTypeId, setJustificationTypeId] = useState<number | null>(null);
  const [freeComment, setFreeComment] = useState('');
  const [isTypeMissing, setIsTypeMissing] = useState(false);
  const [isFreeCommentMissing, setIsFreeCommentMissing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const needsFreeComment = requiresFreeComment(justificationTypeId);

  // Mismo texto de error que el detalle: el énfasis va concatenado, no como segmentos.
  const typeRequiredMessage = [
    translate('attendance:overtimeApproval.detail.justify.typeRequiredEmphasis'),
    translate('attendance:overtimeApproval.detail.justify.typeRequired'),
  ].join('');

  const handleTypeChange = useCallback((value: string | string[]) => {
    const nextTypeId = normalizeNumericSelectValue(
      Array.isArray(value) ? value[0] : value,
    ) ?? null;

    setJustificationTypeId(nextTypeId);
    if (nextTypeId !== null) setIsTypeMissing(false);

    // El comentario solo viaja para el tipo OTRO y el backend rechaza el sobrante, así que al
    // cambiar a un tipo que no lo pide se descarta en vez de quedar oculto y enviarse.
    if (!requiresFreeComment(nextTypeId)) {
      setFreeComment('');
      setIsFreeCommentMissing(false);
    }
  }, [requiresFreeComment]);

  const handleFreeCommentChange = useCallback((value: string) => {
    setFreeComment(value);
    if (value.trim().length > 0) setIsFreeCommentMissing(false);
  }, []);

  const handleSubmit = useCallback(async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (isSubmitting) return;

    const trimmedComment = freeComment.trim();
    const missingType = justificationTypeId === null;
    const missingComment = needsFreeComment && trimmedComment.length === 0;

    if (missingType) setIsTypeMissing(true);
    if (missingComment) setIsFreeCommentMissing(true);
    if (missingType || missingComment) return;

    setIsSubmitting(true);

    try {
      // Solo se cierra en éxito: ante un error del backend se conservan el tipo y el comentario ya
      // tecleados para que el usuario reintente sin rearmar la entrada, igual que el flujo hermano
      // del detalle (`useOvertimeApprovalDetailActions.onJustify`).
      const isConfirmed = await onConfirm(
        justificationTypeId!,
        needsFreeComment ? trimmedComment : undefined,
      );

      if (isConfirmed) closeSidePanel();
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting, freeComment, justificationTypeId, needsFreeComment, onConfirm, closeSidePanel,
  ]);

  const targetLabel = employeeNames.length === 1
    ? employeeNames[0]
    : translate('attendance:overtimeApproval.bulkJustify.selectedCount', {
      count: employeeNames.length,
    });

  return (
    <SidePanelFormLayout onSubmit={handleSubmit} data-testid="overtime-justify-panel">
      <SidePanelFormLayout.Fields>
        <div className="flex flex-col gap-1">
          <Text variant="body-light" color={colors.grays.neutral66}>
            {translate('attendance:overtimeApproval.bulkJustify.employeeLabel')}
          </Text>
          <Text
            variant="body-medium"
            color={colors.primary.dash}
            data-testid="overtime-justify-panel-target"
          >
            {targetLabel}
          </Text>
        </div>

        <Selector
          label={translate('attendance:overtimeApproval.detail.justify.typeLabel')}
          required
          options={typeOptions}
          value={justificationTypeId !== null ? String(justificationTypeId) : ''}
          onChange={handleTypeChange}
          isLoading={isLoadingTypes}
          disabled={isSubmitting}
          placeholder={hasNoTypes
            ? translate('attendance:overtimeApproval.detail.justify.typeEmpty')
            : translate('attendance:overtimeApproval.detail.justify.typePlaceholder')}
          color={isTypeMissing ? 'error' : 'default'}
          supportingText={isTypeMissing ? typeRequiredMessage : undefined}
          data-testid="overtime-justify-panel-type-selector"
        />

        {needsFreeComment && (
          <CommentBox
            value={freeComment}
            onChange={handleFreeCommentChange}
            placeholder={translate('attendance:overtimeApproval.detail.justify.freeCommentPlaceholder')}
            ariaLabel={translate('attendance:overtimeApproval.detail.justify.freeCommentAriaLabel')}
            required
            disabled={isSubmitting}
            maxLength={OVERTIME_JUSTIFICATION_FREE_COMMENT_MAX_LENGTH}
            color={isFreeCommentMissing ? 'error' : 'default'}
            data-testid="overtime-justify-panel-free-comment"
          />
        )}
      </SidePanelFormLayout.Fields>

      <PermissionGuard action="edit">
        <SidePanelFormLayout.Footer>
          {/* No se deshabilita por falta de tipo a propósito: el usuario debe poder presionar para
              que el aviso rojo le explique qué falta, igual que en el detalle. Un catálogo vacío
              SÍ deshabilita: no hay nada que elegir y enviar sin tipo es lo que el SP rechaza. */}
          <PrimaryButton
            type="submit"
            fullWidth
            isLoading={isSubmitting}
            disabled={hasNoTypes || isSubmitting}
            data-testid="overtime-justify-panel-submit"
          >
            {translate('attendance:overtimeApproval.bulkJustify.action')}
          </PrimaryButton>
        </SidePanelFormLayout.Footer>
      </PermissionGuard>
    </SidePanelFormLayout>
  );
};

export default OvertimeJustifyPanel;
